import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Switch,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid2
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { MyElectionResponseDto } from '@thro-clients/polyas-connector-api';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';

import { getStatusLabel } from '../../MyElections/helper';

interface UserElectionCardProps {
  election: MyElectionResponseDto;
  voterStatus: boolean;
  personalizedLink: string | null | undefined;
  onMarkAsVoted: (marked: boolean) => void;
}

export function UserElectionCard({
  election,
  voterStatus,
  personalizedLink,
  onMarkAsVoted
}: UserElectionCardProps) {
  const { t } = useTranslation();
  const [showVoteDialog, setShowVoteDialog] = useState(false);

  const handleConfirmVote = () => {
    if (personalizedLink) {
      window.open(personalizedLink, '_blank', 'noopener,noreferrer');
    }
    setShowVoteDialog(false);
  };

  const statusColorMap: Record<string, 'success' | 'warning' | 'default'> = {
    active: 'success',
    upcoming: 'warning',
    ended: 'default'
  };

  return (
    <Grid2 size={12}>
      <Card
        sx={{
          borderColor: voterStatus ? 'success.light' : undefined,
          bgcolor: voterStatus ? 'success.50' : undefined
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6">{election.title}</Typography>
                <Chip
                  label={getStatusLabel(election.status, t)}
                  color={statusColorMap[election.status]}
                  size="small"
                />
              </Box>
              {election.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {election.description}
                </Typography>
              )}
            </Box>
            {voterStatus && (
              <Chip
                icon={<CheckCircleIcon />}
                label={t(translations.pages.polyas.userElection.voted())}
                color="success"
                size="small"
              />
            )}
          </Box>

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}
          >
            <CalendarTodayIcon fontSize="small" />
            <Typography variant="body2">
              {format(new Date(election.electionStartDate), 'dd.MM.yyyy HH:mm', { locale: de })}
              {' - '}
              {format(new Date(election.electionEndDate), 'dd.MM.yyyy HH:mm', { locale: de })}
            </Typography>
          </Box>

          {election.status === 'active' && !voterStatus && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => setShowVoteDialog(true)}
                >
                  {t(translations.pages.polyas.userElection.voteNow())}
                </Button>
              }
            >
              {t(translations.pages.polyas.userElection.electionActive())}
            </Alert>
          )}

          {election.status === 'future' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(translations.pages.polyas.userElection.electionStartsAt())}{' '}
              <strong>
                {format(new Date(election.electionStartDate), "dd.MM.yyyy 'um' HH:mm 'Uhr'", {
                  locale: de
                })}
              </strong>
            </Alert>
          )}

          {election.status === 'finished' && !voterStatus && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(translations.pages.polyas.userElection.electionEnded())}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              {voterStatus ? (
                <NotificationsOffIcon fontSize="small" />
              ) : (
                <NotificationsIcon fontSize="small" />
              )}
              <Typography variant="body2">
                {voterStatus
                  ? t(translations.pages.polyas.userElection.noReminders())
                  : t(translations.pages.polyas.userElection.remindersActive())}
              </Typography>
            </Box>
            {election.status === 'active' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t(translations.pages.polyas.userElection.markAsVoted())}
                </Typography>
                <Switch
                  checked={voterStatus}
                  onChange={e => onMarkAsVoted(e.target.checked)}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </CardContent>

        <Dialog open={showVoteDialog} onClose={() => setShowVoteDialog(false)}>
          <DialogTitle>{t(translations.pages.polyas.userElection.goToVoting())}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(translations.pages.polyas.userElection.redirectToExternal())}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowVoteDialog(false)}>
              {t(translations.pages.polyas.userElection.cancel())}
            </Button>
            <Button variant="contained" onClick={handleConfirmVote} startIcon={<OpenInNewIcon />}>
              {t(translations.pages.polyas.userElection.goToVotingButton())}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Grid2>
  );
}
